import PropTypes from 'prop-types';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, Divider, Grid, Link, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader } from '../common';
// data
import { OFFICES, telHref } from '../../../data/contact-info';

// ----------------------------------------------------------------------

DetailRow.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  href: PropTypes.string,
};

function DetailRow({ icon, text, href }) {
  const content = (
    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
      {text}
    </Typography>
  );

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          color: 'primary.main',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Iconify icon={icon} width={17} />
      </Box>

      {href ? (
        <Link href={href} underline="hover" sx={{ color: 'inherit', pt: 0.75, minWidth: 0 }}>
          {content}
        </Link>
      ) : (
        <Box sx={{ pt: 0.75, minWidth: 0 }}>{content}</Box>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------

OfficeCard.propTypes = {
  office: PropTypes.object.isRequired,
  index: PropTypes.number,
};

function OfficeCard({ office, index }) {
  return (
    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
      <MotionInView variants={varFadeInUp} transition={{ delay: index * 0.12 }} sx={{ width: '100%' }}>
        <Card
          sx={{
            height: '100%',
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: 'transparent',
                boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.12)}`,
              },
            },
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Iconify icon={office.flag} width={30} />
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              {office.name}
            </Typography>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Stack spacing={{ xs: 1.75, md: 2 }}>
            <DetailRow icon="mdi:address-marker-outline" text={office.address} />
            <DetailRow icon="ic:round-call" text={office.phone} href={telHref(office.phone)} />
            <DetailRow icon="ic:round-mail-outline" text={office.email} href={`mailto:${office.email}`} />
          </Stack>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

ConatctHero.propTypes = {
  title: PropTypes.string,
};

export default function ConatctHero({ title = 'Contact Us', ...other }) {
  return (
    <Box {...other}>
      <SectionHeader
        eyebrow="Get in touch"
        title={title}
        titleComponent="h1"
        description="Visit or call either of our offices, or send us a message and our team will get back to you."
      />

      <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
        {OFFICES.map((office, index) => (
          <OfficeCard key={office.id} office={office} index={index} />
        ))}
      </Grid>
    </Box>
  );
}
